import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Copy, Check } from "lucide-react";
import { useState } from "react";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface MessageListProps {
  messages: Message[];
}

export default function MessageList({ messages }: MessageListProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
        >
          <Card
            className={`max-w-2xl px-4 py-3 ${
              message.role === "user"
                ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-none"
                : "bg-muted rounded-2xl rounded-tl-none"
            }`}
          >
            <div className="space-y-2">
              {message.content.split("\n\n").map((block, idx) => {
                if (block.startsWith("```")) {
                  const lines = block.split("\n");
                  const language = lines[0].replace("```", "").trim();
                  const code = lines.slice(1, -1).join("\n");

                  return (
                    <div
                      key={idx}
                      className="bg-background border border-border rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center justify-between bg-muted px-4 py-2 border-b border-border">
                        <span className="text-xs font-medium text-muted-foreground">
                          {language || "code"}
                        </span>
                        <button
                          onClick={() => copyToClipboard(code, message.id)}
                          className="p-1 hover:bg-background rounded text-muted-foreground hover:text-foreground transition"
                        >
                          {copiedId === message.id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <pre className="p-4 overflow-x-auto text-sm text-foreground">
                        <code>{code}</code>
                      </pre>
                    </div>
                  );
                }

                return (
                  <p key={idx} className="text-sm leading-relaxed">
                    {block}
                  </p>
                );
              })}
            </div>
          </Card>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
